// Clients service - moved from database.js
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  db
} from '../../utils/database/firebase-config';
import cache from '../../utils/dataCache';
import { auditService, AUDIT_MODULES, AUDIT_ACTIONS, AUDIT_TAGS } from '../system/audit';

const clientLabel = (c) => c?.name || c?.companyName || c?.clientId || c?.id || 'client';

export const clientsService = {
  async getClients(companyId) {
    const key = `clients:${companyId}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    cache.set(key, data);
    return data;
  },
  async getClient(clientId, companyId) {
    const clientRef = doc(db, 'clients', clientId);
    const clientSnap = await getDoc(clientRef);

    if (!clientSnap.exists()) {
      throw new Error('Client not found');
    }

    const clientData = clientSnap.data();

    // Verify client belongs to the correct company
    if (companyId && clientData.companyId !== companyId) {
      throw new Error('Client not found or access denied');
    }

    return { id: clientSnap.id, ...clientData };
  },
  async addClient(clientData) {
    const clientsRef = collection(db, 'clients');
    const docRef = await addDoc(clientsRef, { ...clientData, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    cache.invalidatePrefix('clients:');
    auditService.log({
      module: AUDIT_MODULES.CUSTOMER,
      action: AUDIT_ACTIONS.CREATE,
      recordId: docRef.id,
      recordLabel: clientLabel(clientData),
      after: clientData,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.CUSTOMER_CHANGES],
      companyId: clientData.companyId,
      meta: { customerId: docRef.id }
    });
    return docRef.id;
  },
  async updateClient(clientId, clientData) {
    const clientRef = doc(db, 'clients', clientId);
    let before = null;
    try {
      const snap = await getDoc(clientRef);
      if (snap.exists()) before = { id: snap.id, ...snap.data() };
    } catch { /* best effort */ }

    await updateDoc(clientRef, { ...clientData, updatedAt: serverTimestamp() });
    cache.invalidatePrefix('clients:');

    auditService.logUpdate({
      module: AUDIT_MODULES.CUSTOMER,
      recordId: clientId,
      recordLabel: clientLabel({ ...before, ...clientData }),
      before,
      after: { ...(before || {}), ...clientData },
      extraTags: [AUDIT_TAGS.CUSTOMER_CHANGES],
      meta: { customerId: clientId }
    });
    return true;
  },
  async deleteClient(clientId) {
    const clientRef = doc(db, 'clients', clientId);
    let before = null;
    try {
      const snap = await getDoc(clientRef);
      if (snap.exists()) before = { id: snap.id, ...snap.data() };
    } catch { /* best effort */ }

    await deleteDoc(clientRef);
    cache.invalidatePrefix('clients:');

    auditService.log({
      module: AUDIT_MODULES.CUSTOMER,
      action: AUDIT_ACTIONS.DELETE,
      recordId: clientId,
      recordLabel: clientLabel(before),
      before,
      after: null,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.CUSTOMER_CHANGES, AUDIT_TAGS.DELETIONS],
      companyId: before?.companyId,
      meta: { customerId: clientId }
    });
    return true;
  }
};
